import React, { useCallback, useEffect, useState } from 'react';
import { FlatList, useWindowDimensions, View } from 'react-native';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents, AnalyticsSelectionTypes } from 'src/_shared/analyticsEvents';
import { useStore } from 'src/contexts/StoreContext';
import { brand, neutrals } from 'src/theme/colors';
import TextLato from '../../../text/text-lato/TextLato';
import { checkScopes, getAccountInfo, readAccessToken } from 'src/helpers/google.helper';
import { SCOPE_GOOGLE_PHOTO } from 'src/constants/google.constants';
import {
  DYNAMIC_MODAL_VIEWS,
  SELECTED_MENU_ITEMS,
} from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { ICON_NAMES } from '../../../icon/icon.helper';
import { styleDesktop } from './ModalUploadMultisource.style';
import { observer } from 'mobx-react-lite';
import {
  sourcesDesktopNew,
  sourcesDesktopOld,
  sourcesDesktopTopNew,
  sourcesDesktopTopOld,
  SourcesEventType,
} from './ModalUploadMultisource.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { PickerSourceButton } from '../../../buttons/picker-source-button/PickerSourceButton';
import { modalTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups } from '../../../focus-provider/FocusProvider.constants';
import { PrimaryButton } from '../../../buttons/primary-button/PrimaryButton';
import { PrimaryButton as PrimaryButtonNew } from '../../../../editor-v3/components/buttons/primary-button/PrimaryButton';
import { useNavigation } from '@react-navigation/native';
import { ROUTE_NAMES } from 'src/routes/_shared/routes.constants';
import { DynamicModalProps } from '../DynamicModal.types';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { TextIconButton } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton';
import { PickedImage } from '@photosi/expo-photosi-sdk/build/ImageLibrary';
import { pickImages } from 'src/store/_shared/imagePickerUtils';
import braze from 'src/_shared/braze';
import { IconButton } from 'src/editor-v3/components/icon-buttons/icon-button/IconButton';
import { DiscountScale } from '@photosi/expo-photosi-sdk/build/Project';
import { getSourceType } from 'src/helpers/tracking.helper';
import { ModalUploadMultisourceBar } from './_shared/modal-upload-multisource-bar/ModalUploadMultisourceBar';
import { usePickerLogic } from './picker/Picker.logic';
import { LookupChoiceForStep } from '@photosi/expo-photosi-sdk/build/PrintUtils';
import OptionSelector, { OptionSelectorOption } from '@photosi/rn-components/option-selector/OptionSelector';
import { Tile } from 'src/editor-v3/components/tile/Tile';
import { Neutrals } from 'src/theme-v2/colors';
import { getFormattedPrice } from 'src/helpers/price.helper';
import { BUTTON_LAYOUTS, BUTTON_SIZES, BUTTON_TYPES } from 'src/editor-v3/components/buttons/button/Button.constants';
import { STEPS } from 'src/editor-v3/components/upload-picker/UploadSteps';

export interface ModalUploadMultiSourceDesktopProps extends DynamicModalProps {
  discount: DiscountScale[];
  isOnline: boolean;
  selectedCard: string | undefined;
  setSelectedCard: (data: string) => void;
  step: STEPS;
  options: OptionSelectorOption[];
  setSelectedOption: (data: string) => void;
  initialOption: string;
  getCards: LookupChoiceForStep[];
  goBack: () => void;
  onConfirmStep: () => void;
}

export const ModalUploadMultiSourceDesktop = observer((props: ModalUploadMultiSourceDesktopProps) => {
  const {
    project: { activeProject, setShowUploadPhotosBanner },
    i18n,
    dynamicModal: { closeModal, openModal },
    googleAccount: { setAccessToken, setIsUserLogged, setProfilePic, setEmail, setHasGivenAccessToPhoto },
    data: { percentage, isImageShareStarted, setPercentage, setUploadError },
    toastStore,
  } = useStore();
  const {
    dynamicModalVisible,
    dynamicModalLayer: {
      selectedMenuItem,
      setSelectedItem,
      newUploadMultiSourceCallback,
      isUploadingPhoto,
      uploadMultiSourceCallback,
    },
    isOnline,
    selectedCard,
    setSelectedCard,
    step,
    options,
    setSelectedOption,
    initialOption,
    getCards,
    goBack,
    onConfirmStep,
  } = props;

  const [containerWidth, setContainerWidth] = useState(0);

  const { height } = useWindowDimensions();
  const maxHeight = height * 0.6;
  const newRoute = useNavigation().getState()?.routes[0].name === ROUTE_NAMES.EditorNew;
  const sourcesDesktop = newRoute ? sourcesDesktopNew : sourcesDesktopOld;
  const sourcesDesktopTop = newRoute ? sourcesDesktopTopNew : sourcesDesktopTopOld;

  const renderTileItem = useCallback(
    ({ item, index }: { item: LookupChoiceForStep; index: number }) => {
      const CARD_WIDTH = containerWidth * 0.32;
      const isDiscount = item.price != item.fullPrice;
      return (
        <Tile
          code={item.name}
          previewImageUrl={item?.desktopImageUrl ?? ''}
          title={i18n.t(`modal-picker-my-photos:${item.name}`)}
          index={index}
          fullPrice={getFormattedPrice({
            price: item.fullPrice || 0,
            withCurrency: true,
          })}
          isSelected={item.name === selectedCard}
          width={CARD_WIDTH}
          borderColor={Neutrals.neutralGreys.GreyNatural150}
          onPress={() => setSelectedCard(item.name)}
          {...(isDiscount && {
            price: getFormattedPrice({
              price: item.price || 0,
              withCurrency: true,
            }),
          })}
        />
      );
    },
    [selectedCard, containerWidth],
  );

  const handleOnClose = () => {
    setUploadError(false);
    setShowUploadPhotosBanner(false);
    closeModal();
    activeProject?.deselectAllThumbnailImages();
    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.closeClick);
  };

  useEffect(() => {
    const accessToken = readAccessToken();
    if (accessToken) {
      setAccessToken(accessToken);
      getAccountInfo({ accessToken: accessToken }).then(res => {
        setProfilePic(res.picture);
        setEmail(res.email);
      });
      checkScopes(accessToken).then(res => {
        const userHasGivenAccessToPhoto = res.scope.includes(SCOPE_GOOGLE_PHOTO);
        if (userHasGivenAccessToPhoto) {
          setHasGivenAccessToPhoto(true);
          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachSuccess, {
            source_type: SourcesEventType.GooglePhoto,
          });
        } else {
          setHasGivenAccessToPhoto(false);
          amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceAttachError, {
            source_type: SourcesEventType.GooglePhoto,
            error: '',
          });
        }
      });
      setIsUserLogged(true);
    }
  }, []);

  useEffect(() => {
    if (
      dynamicModalVisible &&
      activeProject?.addingProductCount! >= 1 &&
      activeProject?.alreadyAddedProductCount! >= 1 &&
      !activeProject?.isEmpty
    ) {
      closeModal();
    }
  }, [activeProject?.alreadyAddedProductCount, activeProject?.addingProductCount, activeProject?.isEmpty]);

  const loadingPercentageBar = isUploadingPhoto || (percentage > 0 && percentage <= 100) || isImageShareStarted;

  useEffect(() => {
    if (percentage === 100) {
      setTimeout(() => {
        setPercentage(0);
      }, 500);
    }
  }, [percentage]);

  useEffect(() => {
    if (selectedMenuItem !== SELECTED_MENU_ITEMS.Computer && isImageShareStarted) {
      setSelectedItem(SELECTED_MENU_ITEMS.Computer);
    }
  }, [isImageShareStarted]);

  const selectedThumbnails = activeProject?.selectedThumbnails?.length || 0;
  const ctaLabel =
    selectedThumbnails > 0
      ? i18n.t('modal-picker-my-photos:deselect-all')
      : i18n.t('modal-picker-my-photos:select-all');

  const onPressHandlerComputer = async () => {
    const productImages: PickedImage[] = await pickImages({
      allowsMultipleSelection: true,
      i18n,
      errorCallback: ({ errorMessage }) => {
        closeModal();
        toastStore.showErrorToast({
          id: 'alert-uploading-photos-error-toast',
          message: errorMessage,
        });
      },
      newRoute: newRoute,
    });
    if (!!productImages.length) {
      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick, {
        source_type: SourcesEventType.Computer,
      });
      braze.trackEvent(AnalyticsEvents.dynamicPicker.choosePhotosClick);

      if (newRoute) {
        //const autoselect = activeProject!.thumbnailImages.length == 0;
        productImages.forEach(productImage => (productImage.source = SELECTED_MENU_ITEMS.Computer));
        await activeProject?.addImages(productImages);
      } else {
        uploadMultiSourceCallback({ files: productImages, sourceType: SourcesEventType.Computer });
      }

      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourcePhotoUploaded, {
        n_photo: productImages.length,
        source_type: SourcesEventType.Computer,
      });
    }
  };

  const { Picker, pickerTitleKey, pickerDescriptionKey } = usePickerLogic(selectedMenuItem);

  const onPressDelete = () => {
    if (!!activeProject?.selectedThumbnails?.length) {
      openModal(
        {
          dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteAll,
          thumbnailsToDelete: activeProject?.selectedThumbnails?.map(item => item.originalImageId),
        },
        true,
      );
    }
  };

  const isActiveDelete =
    !!activeProject?.selectedThumbnails?.length &&
    activeProject?.selectedThumbnails?.every(item => item.usageQuantity === 0);

  return (
    <View
      style={styleDesktop.container}
      onLayout={e => {
        setContainerWidth(e.nativeEvent.layout.width);
      }}
    >
      <View style={styleDesktop.header}>
        <View style={styleDesktop.left_element}>
          {((step === STEPS.Picker && selectedMenuItem !== SELECTED_MENU_ITEMS.Computer) ||
            (step === STEPS.Picker && !!getCards.length)) && (
            <IconButton
              iconName={ICON_NAMES.ArrowLeft1}
              iconStyle={styleDesktop.backIcon}
              style={styleDesktop.back}
              focusStyle={styleDesktop.backFocus}
              onPress={async () => {
                if (selectedMenuItem !== SELECTED_MENU_ITEMS.Computer) {
                  setSelectedItem(SELECTED_MENU_ITEMS.Computer);
                } else {
                  goBack();
                }
              }}
              testID={modalTestID(TestIDs.BACK)}
              focusId={modalTestID(TestIDs.BACK)}
              focusOrder={1}
              focusGroup={FocusGroups.MODAL_HEADER}
              accessible={true}
              accessibilityLabel={i18n.t('actions:back')}
              accessibilityRole="button"
              size={BUTTON_SIZES.SMALL}
            />
          )}
        </View>
        <View style={styleDesktop.center_element}>
          {step === STEPS.Picker ? (
            <>
              <TextLato style={styleDesktop.title}>{i18n.t(pickerTitleKey)}</TextLato>
              {pickerDescriptionKey && (
                <TextLato style={styleDesktop.description}>{i18n.t(pickerDescriptionKey)}</TextLato>
              )}
            </>
          ) : (
            <TextLato style={styleDesktop.title}>{i18n.t('modal-picker-my-photos:options-title')}</TextLato>
          )}
        </View>
        <View style={styleDesktop.right_element}>
          <IconButton
            onPress={async () => handleOnClose()}
            accessible={true}
            accessibilityLabel={i18n.t('actions:close_x', { x: i18n.t('general:modal') })}
            accessibilityRole="button"
            iconName={ICON_NAMES.Close}
            iconStyle={styleDesktop.closeIcon}
            size={BUTTON_SIZES.SMALL}
            focusOrder={3}
            focusGroup={FocusGroups.MODAL_HEADER}
            focusId={modalTestID(TestIDs.CLOSE)}
            testID={modalTestID(TestIDs.CLOSE)}
          />
        </View>
      </View>
      {step === STEPS.Picker ? (
        <View
          style={[
            activeProject?.thumbnailImages.length === 0 ? styleDesktop.wrapper : styleDesktop.wrapperWithoutBorder,
          ]}
        >
          {selectedMenuItem === SELECTED_MENU_ITEMS.Computer &&
            sourcesDesktopTop.length > 0 &&
            !isUploadingPhoto &&
            activeProject?.thumbnailImages?.length! > 0 && (
              <View style={styleDesktop.topMenu}>
                <View style={styleDesktop.topMenuInner}>
                  <TextLato style={styleDesktop.otherChoisesText} numberOfLines={1}>
                    {i18n.t('upload:upload-from')}
                  </TextLato>
                  {sourcesDesktopTop.map((source, index) => (
                    <PickerSourceButton
                      style={styleDesktop.pickerButtonTop}
                      hoverStyle={styleDesktop.pickerButtonHover}
                      pickerStyle={{ color: brand.main.water }}
                      key={source}
                      source={source}
                      isSelected={false}
                      onPress={async () => {
                        setSelectedItem(source);
                        const source_type = getSourceType(source);
                        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, { source_type });
                        if (source === SELECTED_MENU_ITEMS.Computer) {
                          onPressHandlerComputer();
                        }
                      }}
                      text={i18n.t(`upload:${source}`)}
                      focusId={modalTestID(`choose-${source}`)}
                      focusGroup={FocusGroups.MODAL}
                      focusOrder={index}
                    />
                  ))}
                </View>
                <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center' }}>
                  <TextIconButton
                    focusGroup={FocusGroups.MODAL}
                    focusId={selectedThumbnails ? modalTestID(TestIDs.DESELECT_ALL) : modalTestID(TestIDs.SELECT_ALL)}
                    testID={selectedThumbnails ? modalTestID(TestIDs.DESELECT_ALL) : modalTestID(TestIDs.SELECT_ALL)}
                    focusOrder={10}
                    text={ctaLabel}
                    onPress={async () => {
                      if (selectedThumbnails > 0) {
                        activeProject?.deselectAllThumbnailImages();
                        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.allPhotosSelectClick, {
                          type: AnalyticsSelectionTypes.deselectAll,
                        });
                      } else {
                        activeProject?.selectAllThumbnailImages();
                        amplitude.trackEvent(AnalyticsEvents.dynamicPicker.allPhotosSelectClick, {
                          type: AnalyticsSelectionTypes.selectAll,
                        });
                      }
                    }}
                    size={TEXT_ICON_BUTTON_SIZES.SMALL}
                    type={TEXT_ICON_BUTTON_TYPES.NO_ICON}
                    iconName={ICON_NAMES.AddFrame}
                    textStyle={styleDesktop.textLink}
                    accessible
                    accessibilityRole="button"
                    accessibilityLabel={ctaLabel}
                  />
                  {isActiveDelete && (
                    <>
                      <View style={{ height: 14, width: 1, backgroundColor: brand.main.water }} />
                      <TextIconButton
                        focusGroup={FocusGroups.MODAL}
                        focusId={modalTestID(TestIDs.DELETE_SELECTED)}
                        testID={modalTestID(TestIDs.DELETE_SELECTED)}
                        focusOrder={10}
                        text={i18n.t('modal-picker-my-photos:delete-selected')}
                        onPress={async () => onPressDelete()}
                        size={TEXT_ICON_BUTTON_SIZES.SMALL}
                        type={TEXT_ICON_BUTTON_TYPES.NO_ICON}
                        iconName={ICON_NAMES.AddFrame}
                        textStyle={styleDesktop.textLink}
                        accessible
                        accessibilityRole="button"
                        accessibilityLabel={i18n.t('modal-picker-my-photos:delete-selected')}
                      />
                    </>
                  )}
                </View>
              </View>
            )}
          <View style={[styleDesktop.pickerWrapper, { maxHeight, zIndex: 10 }]}>
            <Picker {...props} isOnline={isOnline} selectedCard={selectedCard} />
          </View>
          {selectedMenuItem === SELECTED_MENU_ITEMS.Computer && activeProject?.thumbnailImages?.length! > 0 && (
            <View style={styleDesktop.footer}>
              <ModalUploadMultisourceBar
                isLoadingPercentageBarVisible={loadingPercentageBar}
                isSpinnerVisible={isImageShareStarted}
                selectedThumbnailsCount={activeProject?.selectedThumbnails.length || 0}
                discountScales={props.discount}
                percentage={percentage}
              />

              <View style={styleDesktop.buttonFooter}>
                <PrimaryButton
                  text={i18n.t('modal-picker-my-photos:confirm')}
                  focusGroup={FocusGroups.MODAL}
                  focusId={modalTestID(TestIDs.CONFIRM)}
                  testID={modalTestID(TestIDs.CONFIRM)}
                  focusOrder={(activeProject?.thumbnailImages?.length ?? 0) + 11}
                  style={{ paddingHorizontal: 50 }}
                  disabled={activeProject?.selectedThumbnails.length === 0 || ![0, 100].includes(percentage)}
                  onPress={async () => {
                    const googlePhotoCounter = activeProject?.selectedThumbnails.filter(
                      thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.GooglePhoto,
                    )?.length;
                    const localPhotoCounter = activeProject?.selectedThumbnails.filter(
                      thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Computer,
                    )?.length;
                    const smartphonePhotoCounter = activeProject?.selectedThumbnails.filter(
                      thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.Smartphone,
                    )?.length;
                    const familyFriendsPhotoCounter = activeProject?.selectedThumbnails.filter(
                      thumbnail => thumbnail.source === SELECTED_MENU_ITEMS.FamilyFriends,
                    )?.length;
                    amplitude.trackEvent(AnalyticsEvents.dynamicPicker.continueClick, {
                      n_photo: activeProject?.selectedThumbnails.length,
                      n_photos_facebook: 0,
                      n_photos_gphoto: googlePhotoCounter ? googlePhotoCounter : 0,
                      n_photos_local: localPhotoCounter ? localPhotoCounter : 0,
                      n_photos_smartphone: smartphonePhotoCounter ? smartphonePhotoCounter : 0,
                      n_photos_family_friends: familyFriendsPhotoCounter ? familyFriendsPhotoCounter : 0,
                    });
                    closeModal();
                    newUploadMultiSourceCallback({
                      fileIds: activeProject?.selectedThumbnails.map(t => t.originalImageId),
                    });
                  }}
                >
                  {({ disabled }) => (
                    <TextLato style={[styleDesktop.counter, disabled && { color: neutrals.neutralGrays.ash }]}>
                      {activeProject?.selectedThumbnails.length.toString()}
                    </TextLato>
                  )}
                </PrimaryButton>
              </View>
            </View>
          )}
          {selectedMenuItem === SELECTED_MENU_ITEMS.Computer &&
            sourcesDesktop.length > 0 &&
            !isUploadingPhoto &&
            activeProject?.thumbnailImages.length === 0 && (
              <View style={styleDesktop.sideMenu}>
                <TextLato style={styleDesktop.otherChoisesText} numberOfLines={1}>
                  {i18n.t('upload:or-other-choises')}
                </TextLato>
                {sourcesDesktop.map((source, index) => (
                  <PickerSourceButton
                    style={styleDesktop.pickerButton}
                    hoverStyle={styleDesktop.pickerButtonHover}
                    pickerStyle={{ color: brand.main.water }}
                    key={source}
                    source={source}
                    isSelected={false}
                    onPress={async () => {
                      setSelectedItem(source);
                      const source_type = getSourceType(source);
                      amplitude.trackEvent(AnalyticsEvents.dynamicPicker.sourceClick, { source_type });
                    }}
                    text={i18n.t(`upload:${source}`)}
                    focusId={modalTestID(`choose-${source}`)}
                    testID={modalTestID(`choose-${source}`)}
                    focusGroup={FocusGroups.MODAL}
                    focusOrder={10 + index}
                  />
                ))}
              </View>
            )}
        </View>
      ) : (
        <View style={styleDesktop.wrapperOptions}>
          <View style={{ alignSelf: 'center' }}>
            <OptionSelector
              options={options}
              onChange={value => setSelectedOption(String(value))}
              defaultValue={initialOption}
            />
          </View>
          <FlatList
            data={getCards}
            horizontal={true}
            keyExtractor={item => item.productConfigurationId}
            showsHorizontalScrollIndicator={false}
            style={{ flexGrow: 0 }}
            contentContainerStyle={{
              flexGrow: 1,
              justifyContent: 'center',
              gap: 12,
            }}
            renderItem={renderTileItem}
            extraData={selectedCard}
          />
          <View style={{ flex: 1, alignItems: 'center' }}>
            <View style={styleDesktop.buttonFooterCentered}>
              <PrimaryButtonNew
                text={i18n.t('modal-picker-my-photos:continue')}
                focusGroup={FocusGroups.MODAL}
                focusId={modalTestID(TestIDs.CONFIRM)}
                testID={modalTestID(TestIDs.CONFIRM)}
                focusOrder={(activeProject?.thumbnailImages?.length ?? 0) + 11}
                onPress={async () => onConfirmStep()}
                size={BUTTON_SIZES.REGULAR}
                type={BUTTON_TYPES.DEFAULT}
                layout={BUTTON_LAYOUTS.FILL}
              />
            </View>
          </View>
        </View>
      )}
    </View>
  );
});
